'use client';

import { useState, useEffect, useCallback } from 'react';
import { EditModal } from '@/components/ui/edit-modal';
import { CategoryRangeField } from '@/features/tournament-wizard/blocks';
import type { Category, Sport } from '@/types';

interface EditCategoriesModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentMinCategory: Category;
  currentMaxCategory: Category;
  currentForceSum: boolean;
  sport: Sport;
  onSave: (categoriesData: {
    minCategory: Category;
    maxCategory: Category;
    forceSum: boolean;
  }) => Promise<boolean>;
  isLoading: boolean;
}

export function EditCategoriesModal({
  isOpen,
  onClose,
  currentMinCategory,
  currentMaxCategory,
  currentForceSum,
  sport,
  onSave,
  isLoading,
}: EditCategoriesModalProps) {
  const [minCategory, setMinCategory] = useState<Category>(currentMinCategory);
  const [maxCategory, setMaxCategory] = useState<Category>(currentMaxCategory);
  const [forceSum, setForceSum] = useState(currentForceSum);

  useEffect(() => {
    if (isOpen) {
      setMinCategory(currentMinCategory);
      setMaxCategory(currentMaxCategory);
      setForceSum(currentForceSum);
    }
  }, [isOpen, currentMinCategory, currentMaxCategory, currentForceSum]);

  const handleCategoryChange = useCallback(
    (min: Category, max: Category, force: boolean) => {
      setMinCategory(min);
      setMaxCategory(max);
      setForceSum(force);
    },
    [],
  );

  const handleSave = async () => {
    await onSave({ minCategory, maxCategory, forceSum });
  };

  return (
    <EditModal
      isOpen={isOpen}
      onClose={onClose}
      title="Editar Categorías"
      onSave={handleSave}
      isLoading={isLoading}
      size="large"
    >
      <div className="max-h-[70vh] overflow-y-auto py-4">
        <CategoryRangeField
          layout="shell"
          sport={sport}
          onChange={handleCategoryChange}
          initialMinCategory={currentMinCategory}
          initialMaxCategory={currentMaxCategory}
          initialForceSum={currentForceSum}
        />
      </div>
    </EditModal>
  );
}
